import React, { useState, useEffect, useRef } from 'react';
import withSearchBar from 'global/components/withSearchBar';
import { useSelector } from 'react-redux';
import { get, set, size, uniqBy } from 'global/lib/lodash';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { List, Button, Dialog, AppBar, Toolbar, IconButton, Typography, Icon, MenuItem, Select, FormControlLabel, Checkbox, FormGroup } from '@mui/material';
import UserRow, { RowHeader } from './userrow';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { withRouter } from 'react-router-dom';
import { buildFilters } from 'global/utils/filter';
import useStyles from './styles';
import Profile from '../profile';
import Modal from 'global/components/modal';
import { Loader } from 'global/styles';
import { UserPermission } from 'global/lib/filter';
import { Access } from 'global/components/droprealms';
import { DivisionIcon } from 'global/components/PerformIcons';
import sendData from 'redux/actions/sendData';
import useDebounce from 'global/hooks/useDebounce';
import SnackAlerts from 'global/components/snack-alerts';
import { findServerPerm, reverseUserAppPermissions, appPermissionToServer, _size } from 'global/utils';
import permissionGate from 'global/components/permission-gate';
import { useHistory } from 'react-router-dom';
import { getRealmIcon } from 'global/components/droprealms';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';

const pageSize = 25;
const include = 'realm,realms,permissions,userGroups';

export const DROPOPTS = [
  { title: 'Add Selected Values', value: 0 },
  { title: 'Remove Selected Values', value: 1 },
  { title: 'Overwrite Existing Values with Selected Values', value: 2 },
];

export default permissionGate(
  withRouter(
    withSearchBar((props) => {
      const [selected, setSelected] = useState({});
      const [alerts, setAlerts] = useState();
      const [activeUser, setActiveUser] = useState();
      const [modal, setModal] = useState();
      const selectedDrop = useRef(0);
      const selectedCheck = useRef({});
      const selRealms = useRef({});
      const notUserRef = useRef({
        currentPage: 1,
        selected: {},
      });
      const [allUsers, setAllUsers] = useState([]);
      const [displayUsers, setDisplayUsers] = useState([]);
      const [loading, setLoading] = useState(false);
      const page = useRef(1);
      const totalUsers = useRef([]);
      const filter = useSelector((state) => get(state, 'filter.users.manage'));
      const { me, realm: hasRealm } = useUser();
      const classes = useStyles();
      const debouncedSearch = useDebounce(filter.searchstring, 1000);
      const { filterPermissionsByNav } = useNavSettings();

      const isInactive = get(filter, 'status') === 'inactive';
      const hasPermFilters = size(get(filter, 'permission')) > 0;
      const usePermUsers = hasPermFilters;
      const hasClient = get(filter, 'client_id') ? true : false;
      const vendor = props.vendors.data.find((f) => f.id === get(filter, 'client_id'));
      const clientId = get(vendor, 'clientId');
      const history = useHistory();

      const filterProps = () => {
        const filts = buildFilters(filter, {
          includeRealm: true,
          convertName: { searchstring: 'name' },
        });

        //#region client id filter gets set to realm, and cleared from the req as its not a valid filter
        const meRealm = get(me, 'realm.id');
        set(filts, 'filter.includes.realms.realm_id', meRealm);
        set(filts, 'filter.user_realm_id', meRealm); //Not sure if this should be here
        const cid = get(filts, 'filter.client_id');
        if (cid) {
          set(filts, 'filter.user_realm_id', meRealm);
          set(filts, 'filter.includes.realms.realm_id', clientId);
          set(filts, 'filter.realm', clientId);
        }
        //#endregion
        if (get(filts, 'filter.status') === 'inactive') {
          set(filts, 'filter.onlyTrashed', 1);
        }
        set(filts, 'filter.client_id', undefined);
        set(filts, 'filter.status', undefined);

        if (usePermUsers) {
          set(
            filts,
            'filter.all_permissions',
            appPermissionToServer(filter.permission)
              .map((f) => f.replace('*', meRealm))
              .join(',')
          );
        }
        set(filts, 'filter.permission', undefined);

        return filts;
      };

      const buildParams = (isFirst = false) => {
        if (isFirst) {
          return [{ url: `/users` }, { build: ['users'], waitFor: null, params: { ...filterProps(), page: { size: pageSize, number: 1 }, include } }];
        } else {
          return [
            { url: `/users` },
            {
              build: ['users'],
              waitFor: undefined,
              useEffect: true,
              params: {
                ...{ page: { size: pageSize, number: page.current }, include },
                ...filterProps(),
              },
            },
          ];
        }
      };

      const [userParams, setUserParams] = useState(buildParams(true));

      // const realmData = useRetrieveData({ url: '/realms' }, { build: ['realms'], params: { filter: { tier: 'division' } } });
      const allPerms = useRetrieveData({ url: `/permissions` }, { build: ['permissions'] });
      const allUserGroups = useRetrieveData({ url: `/user-groups` }, { build: ['userGroups'] });
      const users = useRetrieveData(...userParams);
      const notUsers = useRetrieveData(
        { url: '/users' },
        {
          useEffect: true,
          build: ['users'],
          params: {
            page: { size: pageSize, number: get(notUserRef, 'current.currentPage') },
            include,
            filter: {
              not_realm: clientId,
              user_realm_id: get(me, 'realm.id'),
            },
          },
        }
      );

      const filterUsers = (usrs) => {
        const str = debouncedSearch.trim().toLowerCase();
        setDisplayUsers(
          [...(usrs || allUsers)].filter((u) => {
            let pass = false;
            if (get(u, 'name').toLowerCase().indexOf(str) > -1) {
              pass = true;
            }

            if (get(u, 'email').toLowerCase().indexOf(str) > -1) {
              pass = true;
            }

            if (pass && isInactive) {
              pass = u.status === 0;
            }

            return pass;
          })
        );
      };

      useEffect(() => {
        if (!users.loading) {
          setLoading(true);

          const data = users.data;
          set(totalUsers, 'current', [...get(totalUsers, 'current', []), ...data]);
          setAllUsers(data);
          filterUsers(data);
          setLoading(false);
        }
      }, [users.loading]);

      useEffect(() => {
        filterUsers();
      }, [debouncedSearch]);

      useEffect(() => {
        page.current = 1;
        set(totalUsers, 'current', []);
        setUserParams(buildParams());
        setSelected({});
        filterUsers();
      }, [filter, hasRealm]);

      useEffect(() => {
        if (!activeUser) {
          users.update();
        }
      }, [activeUser]);

      useEffect(() => {
        if (get(modal, 'title') === 'ADD USER TO CLIENT') {
          accessScreenZero();
        }
      }, [notUsers.data, notUsers.loading]);

      const lastPage = get(users.meta, 'page.last-page', null);
      const updatePage = (newPage) => {
        if (newPage <= lastPage) {
          page.current = newPage;
          setUserParams(buildParams());
        }
      };

      const permDropDown = (onChange) => {
        const value = get(selectedDrop, 'current', 0);
        return (
          <Select
            size="small"
            variant="outlined"
            value={value}
            onChange={(e) => {
              selectedDrop.current = e.target.value;
              setModal((m) => ({ ...m }));
              onChange && onChange();
            }}
          >
            {DROPOPTS.map((opt, i) => (
              <MenuItem key={i} {...opt}>
                {opt.title}
              </MenuItem>
            ))}
          </Select>
        );
      };

      const onSelectAll = () => {
        if (Object.keys(selected).length > 0) {
          setSelected({});
        } else {
          const obj = {};
          const umap = get(totalUsers, 'current', []);
          umap.map((du) => (obj[du.id] = du));
          setSelected(obj);
        }
      };

      const onSelectItem = (p) => {
        const sel = { ...selected };
        const { item } = p;
        const exists = get(selected, item.id);
        if (exists) {
          delete sel[item.id];
        } else {
          set(sel, item.id, item);
        }
        setSelected(sel);
      };

      const onSelectRow = (p) => {
        const { push, location } = history;
        push(`${location.pathname}?user=${get(p, 'item.id')}`);
        setActiveUser(p.item);
      };

      const headerProps = () => {
        const allSelected = Object.keys(selected).length > 0 && Object.keys(selected).length === size(users.data);
        return {
          allSelected,
          selectAll: onSelectAll,
          hasClient,
          indeterminate: !allSelected && Object.keys(selected).length > 0,
        };
      };

      const setToLoader = () => {
        setModal((d) => ({
          ...d,
          content: () => (
            <div className={classes.centerLoader}>
              <Loader size={40} />
            </div>
          ),
        }));
      };

      const closeModal = (clearSelected) => {
        set(selectedCheck, 'current', {});
        set(selectedDrop, 'current', 0);
        set(selRealms, 'current', {});
        set(notUserRef, 'current', { currentPage: 1, selected: {} });
        if (clearSelected) {
          setSelected({});
        }
        setModal();
      };

      const onDeactivate = () => {
        const finish = async () => {
          setToLoader();
          await Promise.all(
            Object.keys(selected).map(async (k) => {
              const user = selected[k];
              await sendData({
                url: `/users/${user.id}`,
                method: 'patch',
                data: {
                  data: {
                    id: user.id.toString(),
                    type: 'users',
                    attributes: {
                      archived: true,
                    },
                  },
                },
              });
            })
          );
          await users.update();
          clear();
          closeModal(true);
        };

        setModal({
          open: true,
          title: 'DEACTIVATE USERS',
          content: () => (
            <div className={classes.modalContent}>
              <div className={classes.modalTitle}>{`Deactivate these ${Object.keys(selected).length} users`}</div>
              {Object.keys(selected).map((k) => {
                const u = selected[k];
                return <div className={classes.nameRow} key={k}>{`${get(u, 'name')}, ${get(u, 'email')}`}</div>;
              })}
            </div>
          ),
          buttons: [{ title: 'DE-ACTIVATE', onClick: finish }],
          onClose: () => closeModal(),
        });
      };

      const onActivate = () => {
        const finish = async () => {
          setToLoader();
          await Promise.all(
            Object.keys(selected).map(async (k) => {
              const user = selected[k];
              await sendData({
                url: `/users/${user.id}`,
                method: 'patch',
                data: {
                  data: {
                    id: user.id.toString(),
                    type: 'users',
                    attributes: {
                      archived: false,
                    },
                  },
                },
              });
            })
          );
          await users.update();
          clear();
          closeModal(true);
        };

        setModal({
          open: true,
          title: 'ACTIVATE USERS',
          content: () => (
            <div className={classes.modalContent}>
              <div className={classes.modalTitle}>{`Activate these ${Object.keys(selected).length} users`}</div>
              {Object.keys(selected).map((k) => {
                const u = selected[k];
                return <div className={classes.nameRow} key={k}>{`${get(u, 'name')}, ${get(u, 'email')}`}</div>;
              })}
            </div>
          ),
          buttons: [{ title: 'ACTIVATE', onClick: finish }],
          onClose: () => closeModal(),
        });
      };

      const permScreenOne = () => {
        setModal({
          open: true,
          title: 'SET PERMISSIONS',
          content: () => (
            <div className={classes.formContainer}>
              {permDropDown()}

              <FormGroup>
                {filterPermissionsByNav().map((up, i) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={get(selectedCheck, ['current', up.value])}
                          onChange={(e) => {
                            set(selectedCheck, ['current', up.value], e.target.checked);
                            setModal((m) => ({ ...m }));
                          }}
                        />
                      }
                      label={up.title}
                    />
                  );
                })}
              </FormGroup>
            </div>
          ),
          buttons: [
            { title: 'BACK', disabled: true },
            { title: 'NEXT', onClick: permScreenTwo },
            { title: 'SUBMIT', disabled: true },
          ],
          onClose: () => closeModal(),
        });
      };

      const permScreenTwo = () => {
        const finish = async () => {
          try {
            setToLoader();
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const user = selected[k];
                const permissions = (() => {
                  const sel = get(selectedDrop, 'current', 0);
                  const selPerms = reverseUserAppPermissions(selectedCheck.current);
                  if (sel === 0) {
                    //add
                    return uniqBy([...user.permissions, ...selPerms.map((up) => findServerPerm(allPerms.data, `${up.match}.${get(user, 'realm.id')}`))], 'id');
                  } else if (sel === 1) {
                    //remove
                    const toRemove = selPerms.map((up) => findServerPerm(allPerms.data, `${up.match}.${get(user, 'realm.id')}`).id);
                    return user.permissions.filter((p) => toRemove.indexOf(p.id) === -1);
                  } else if (sel === 2) {
                    //overwrite, same thing as add basically, we don't want to overwrite what else is there because it might be more then the app shows
                    return uniqBy(
                      [
                        ...user.permissions.filter((p) => !UserPermission.find((up) => p.name.indexOf(up.match) > -1)),
                        ...selPerms.map((up) => findServerPerm(allPerms.data, `${up.match}.${get(user, 'realm.id')}`)),
                      ],
                      'id'
                    );
                  }
                })();
                await sendData({
                  url: `/users/${user.id}?include=permissions,realms`,
                  method: 'patch',
                  data: {
                    data: {
                      type: 'users',
                      id: user.id,
                      relationships: {
                        permissions: {
                          data: permissions.map((r) => ({ type: 'permissions', id: get(r, 'id') })),
                        },
                      },
                    },
                  },
                });
              })
            );
            await users.update();
            clear();
            closeModal(true);
          } catch (err) {
            setAlerts({
              message: `Error: ${err.message}`,
              severity: 'error',
            });
            permScreenTwo();
          }
        };

        const getText = () => {
          const selDrop = get(selectedDrop, 'current');
          const _selCheck = get(selectedCheck, 'current');
          const selCheck = UserPermission.filter((up) => get(_selCheck, up.value));

          const selCheckLength = selCheck.length;

          const keyword = selDrop === 0 ? 'Add' : selDrop === 1 ? 'Remove' : 'Overwrite';
          const permText = `${keyword} ${selCheckLength > 1 ? 'these' : 'this'} ${selCheckLength} permission${selCheckLength > 1 ? 's' : ''}:`;

          const userLength = Object.keys(selected).length;

          return {
            permText,
            perms: selCheck.map((up) => up.title).join(', '),
            userTitle: `${selDrop === 0 ? 'To' : selDrop === 1 ? 'From' : 'On'} ${userLength > 1 ? 'these' : 'this'} ${userLength} user${userLength > 1 ? 's' : ''}:`,
            users: Object.keys(selected).map((k) => `${get(selected, [k, 'name'])}, ${get(selected, [k, 'email'])}`),
          };
        };

        const t = getText();

        setModal({
          open: true,
          title: 'SET PERMISSIONS',
          content: () => (
            <div className={classes.modalContent}>
              <div className={classes.modalTitle}>{t.permText}</div>
              <div className={classes.nameRow}>{t.perms}</div>
              <div className={classes.modalTitle}>{t.userTitle}</div>
              {t.users.map((k) => (
                <div className={classes.nameRow} key={k}>
                  {k}
                </div>
              ))}
            </div>
          ),
          buttons: [
            { title: 'BACK', onClick: permScreenOne },
            { title: 'NEXT', disabled: true },
            { title: 'SUBMIT', onClick: finish },
          ],
          onClose: () => closeModal(),
        });
      };

      const ugScreenOne = () => {
        setModal({
          open: true,
          title: 'SET USER GROUPS',
          content: () => (
            <div className={classes.formContainer}>
              {permDropDown()}

              <FormGroup>
                {allUserGroups.data.map((up, i) => {
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={get(selectedCheck, ['current', up.id])}
                          onChange={(e) => {
                            set(selectedCheck, ['current', up.id], e.target.checked);
                            setModal((m) => ({ ...m }));
                          }}
                        />
                      }
                      label={up.name}
                    />
                  );
                })}
              </FormGroup>
            </div>
          ),
          buttons: [
            { title: 'BACK', disabled: true },
            { title: 'NEXT', onClick: ugScreenTwo },
            { title: 'SUBMIT', disabled: true },
          ],
          onClose: () => closeModal(),
        });
      };

      const ugScreenTwo = () => {
        const finish = async () => {
          try {
            setToLoader();
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const user = selected[k];
                const userGroups = (() => {
                  const sel = get(selectedDrop, 'current', 0);
                  const selPerms = allUserGroups.data.filter(d => get(selectedCheck.current, d.id));
                  if (sel === 0) {
                    //add
                    return uniqBy([...user.userGroups, ...selPerms]);
                  } else if (sel === 1) {
                    //remove
                    const toRemove = selPerms.map(u => u.id);
                    return user.userGroups.filter((p) => toRemove.indexOf(p.id) === -1);
                  } else if (sel === 2) {
                    //overwrite, same thing as add basically, we don't want to overwrite what else is there because it might be more then the app shows
                    return uniqBy(
                      selPerms,
                      'id'
                    );
                  }
                })();
                await sendData({
                  url: `/users/${user.id}?include=permissions,realms`,
                  method: 'patch',
                  data: {
                    data: {
                      type: 'users',
                      id: user.id,
                      relationships: {
                        userGroups: {
                          data: userGroups.map((r) => ({ type: 'user-groups', id: `${get(r, 'id')}` })),
                        },
                      },
                    },
                  },
                });
              })
            );
            await users.update();
            clear();
            closeModal(true);
          } catch (err) {
            setAlerts({
              message: `Error: ${err.message}`,
              severity: 'error',
            });
            permScreenTwo();
          }
        };

        const getText = () => {
          const selDrop = get(selectedDrop, 'current');
          const _selCheck = get(selectedCheck, 'current');
          const selCheck = allUserGroups.data.filter((up) => get(_selCheck, up.id));

          const selCheckLength = selCheck.length;

          const keyword = selDrop === 0 ? 'Add' : selDrop === 1 ? 'Remove' : 'Overwrite';
          const permText = `${keyword} ${selCheckLength > 1 ? 'these' : 'this'} ${selCheckLength} user group${selCheckLength > 1 ? 's' : ''}:`;

          const userLength = Object.keys(selected).length;

          return {
            permText,
            perms: selCheck.map((up) => up.name).join(', '),
            userTitle: `${selDrop === 0 ? 'To' : selDrop === 1 ? 'From' : 'On'} ${userLength > 1 ? 'these' : 'this'} ${userLength} user${userLength > 1 ? 's' : ''}:`,
            users: Object.keys(selected).map((k) => `${get(selected, [k, 'name'])}, ${get(selected, [k, 'email'])}`),
          };
        };

        const t = getText();

        setModal({
          open: true,
          title: 'SET USER GROUPS',
          content: () => (
            <div className={classes.modalContent}>
              <div className={classes.modalTitle}>{t.permText}</div>
              <div className={classes.nameRow}>{t.perms}</div>
              <div className={classes.modalTitle}>{t.userTitle}</div>
              {t.users.map((k) => (
                <div className={classes.nameRow} key={k}>
                  {k}
                </div>
              ))}
            </div>
          ),
          buttons: [
            { title: 'BACK', onClick: ugScreenOne },
            { title: 'NEXT', disabled: true },
            { title: 'SUBMIT', onClick: finish },
          ],
          onClose: () => closeModal(),
        });
      };

      const clear = () => {
        selRealms.current = {};
        setSelected({});
      };

      const accessScreenZero = () => {
        setModal({
          open: true,
          title: 'ADD USER TO CLIENT',
          content: (
            <div style={{ overflow: 'hidden' }}>
              <ListWithPages
                handlePageChange={(newPage) => {
                  if (newPage <= get(notUsers, 'meta.page.last-page', null)) {
                    set(notUserRef, 'current.currentPage', newPage);
                    accessScreenZero();
                  }
                }}
                {...notUsers}
                {...notUserRef.current}
                hasClient
                basicDisplay
                onSelectItem={(s) => {
                  const _selected = get(notUserRef, 'current.selected', {});
                  const sel = { ..._selected };
                  const { item } = s;
                  const exists = get(_selected, item.id);
                  if (exists) {
                    delete sel[item.id];
                  } else {
                    set(sel, item.id, item);
                  }
                  set(notUserRef, 'current.selected', sel);
                  accessScreenZero();
                }}
              />
            </div>
          ),
          buttons: [
            { title: 'BACK', disabled: true },
            { title: 'NEXT', disabled: !size(notUsers.data), onClick: () => accessScreenOne({ addFromClient: true }) },
            { title: 'SUBMIT', disabled: true },
          ],
          onClose: () => closeModal(),
        });
      };

      const accessScreenOne = (opts = {}) => {
        const sRealms = get(selRealms, 'current');
        const realms = Object.keys(sRealms).map((k) => sRealms[k]);
        const cid = get(filter, 'client_id');
        const vend = props.vendors.data.find((v) => v.id === cid);
        const allRealms = hasClient && vend ? [get(vend, 'client'), ...get(vend, 'realms', [])] : undefined;

        setModal({
          open: true,
          title: 'SET ACCESS',
          content: (
            <div className={classes.formContainer}>
              {permDropDown(accessScreenOne)}

              <Access 
                allRealms={allRealms} 
                canBackOutOfBuilder={!allRealms} 
                canOnlySelectUsersBuilder 
                currentRealm={!hasClient && hasRealm} 
                realms={realms || []} 
                onSelectedChange={(s) => set(selRealms, 'current', s)} 
                onlyTiers={['builder', 'division']}
              />
            </div>
          ),
          buttons: [
            { title: 'BACK', disabled: !opts.backEnabled, onClick: () => accessScreenZero() },
            { title: 'NEXT', onClick: () => accessScreenTwo(opts) },
            { title: 'SUBMIT', disabled: true },
          ],
          onClose: () => closeModal(),
        });
      };

      const accessScreenTwo = (opts = {}) => {
        const notUserSelected = get(notUserRef, 'current.selected');
        const _selected = _size(notUserSelected) > 0 ? notUserSelected : selected;
        const finish = async () => {
          try {
            setToLoader();
            await Promise.all(
              Object.keys(_selected).map(async (k) => {
                const user = { ..._selected[k] };
                const sel = get(selectedDrop, 'current', 0);
                const realms = Object.keys(selRealms.current).map((k) => selRealms.current[k]);

                if (sel === 2) {
                  //overwrite
                  await sendData({
                    url: `/users/${user.id}`,
                    method: 'patch',
                    data: {
                      data: {
                        type: 'users',
                        id: user.id,
                        relationships: {
                          realms: {
                            data: realms.map((r) => ({ type: 'realms', id: get(r, 'id') })),
                          },
                        },
                      },
                    },
                  });
                } else {
                  await sendData({
                    url: `/users/${user.id}/realms/${sel === 0 ? 'add' : 'remove'}`,
                    method: 'post',
                    data: {
                      data: {
                        type: 'users',
                        id: user.id,
                        attributes: {
                          realms: realms.map((r) => get(r, 'id')),
                        },
                      },
                    },
                  });
                }
              })
            );
            await Promise.all([await users.update(), await notUsers.update()]);
            clear();
            closeModal(true);
          } catch (err) {
            setAlerts({
              message: `Error: ${err.message}`,
              severity: 'error',
            });
            accessScreenTwo(opts);
          }
        };

        const getText = () => {
          const selDrop = get(selectedDrop, 'current');
          const selCheck = get(selRealms, 'current');

          const selCheckLength = Object.keys(selCheck).length;

          const keyword = selDrop === 0 ? 'Add' : selDrop === 1 ? 'Remove' : 'Overwrite';
          const tiers = {};

          Object.keys(selCheck).map((k) => {
            const tier = get(selCheck, [k, 'tier']);
            set(tiers, tier, get(tiers, tier, 0) + 1);
          });

          const selTier = Object.keys(tiers)[0];

          const permText = opts.addFromClient
            ? `Add access to this client: `
            : `${keyword} access to ${selCheckLength > 1 ? 'these' : 'this'} ${selCheckLength} ${selTier}${selCheckLength > 1 ? 's' : ''}:`;

          const userLength = Object.keys(_selected).length;

          return {
            permText,
            perms: opts.addFromClient
              ? [
                  <div className={classes.nameRow}>
                    {getRealmIcon(get(vendor, 'client'))}
                    <span style={{ marginLeft: 5 }}>{get(vendor, ['client', 'name'])}</span>
                  </div>,
                ]
              : Object.keys(selCheck).map((k) => {
                  return (
                    <div className={classes.nameRow}>
                      <DivisionIcon style={{ marginRight: 5 }} />
                      {get(selCheck, [k, 'name'])}
                    </div>
                  );
                }),
            userTitle: `${selDrop === 0 ? 'To' : selDrop === 1 ? 'From' : 'On'} ${userLength > 1 ? 'these' : 'this'} ${userLength} user${userLength > 1 ? 's' : ''}:`,
            users: Object.keys(_selected).map((k) => `${get(_selected, [k, 'name'])}, ${get(_selected, [k, 'email'])}`),
          };
        };

        const t = getText();

        setModal({
          open: true,
          title: 'SET ACCESS',
          content: () => (
            <div className={classes.modalContent}>
              <div className={classes.modalTitle}>{t.permText}</div>
              {t.perms.map((d) => d)}
              <div className={classes.modalTitle}>{t.userTitle}</div>
              {t.users.map((k) => (
                <div className={classes.nameRow} key={k}>
                  {k}
                </div>
              ))}
            </div>
          ),
          buttons: [
            { title: 'BACK', onClick: accessScreenOne },
            { title: 'NEXT', disabled: true },
            { title: 'SUBMIT', onClick: finish },
          ],
          onClose: () => closeModal(),
        });
      };

      const hasSelected = Object.keys(selected).length > 0;

      return (
        <div>
          {(hasSelected || hasClient) && (
            <div className={classes.selectedContainer}>
              <div className={classes.selectedText}>{`${Object.keys(selected).length} Selected`}</div>

              <div className={classes.buttonContainer}>
                {hasClient && !hasSelected && (
                  <Button onClick={accessScreenZero} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>label</Icon>}>
                    ADD USER TO CLIENT
                  </Button>
                )}
                {!hasClient && !isInactive && (
                  <Button onClick={onDeactivate} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>cancel</Icon>}>
                    DE-ACTIVATE
                  </Button>
                )}
                {!hasClient && isInactive && (
                  <Button onClick={onActivate} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>person</Icon>}>
                    ACTIVATE
                  </Button>
                )}
                {!hasClient && !isInactive && (
                  <Button onClick={permScreenOne} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>label</Icon>}>
                    SET PERMISSIONS
                  </Button>
                )}
                {hasSelected && !isInactive && (
                  <Button className="accessButton" onClick={() => accessScreenOne()} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>label</Icon>}>
                    SET ACCESS
                  </Button>
                )}
                {hasSelected && !isInactive && (
                  <Button className="accessButton" onClick={() => ugScreenOne()} variant="contained" color={'secondary'} startIcon={<Icon className={classes.xsHide}>label</Icon>}>
                    SET GROUP
                  </Button>
                )}
              </div>
            </div>
          )}

          <ListWithPages
            handlePageChange={updatePage}
            data={displayUsers}
            loading={loading || users.loading}
            header={<RowHeader {...headerProps()} />}
            selected={selected}
            hasClient={hasClient}
            onSelectItem={onSelectItem}
            onSelectRow={onSelectRow}
            currentPage={page.current}
          />

          <Dialog className={classes.dialog} fullScreen disableScrollLock disableEscapeKeyDown open={!!activeUser} onClose={() => setActiveUser()}>
            <AppBar color="secondary" className={classes.appBar}>
              <Toolbar className={classes.toolbar}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setActiveUser()}
                  aria-label="Close"
                  size="large">
                  <Icon>close</Icon>
                </IconButton>

                <div className={classes.barTitle}>
                  <Typography variant="h5" className={classes.title}>
                    <Icon style={{ marginRight: 5 }}>account_circle</Icon>
                    {get(activeUser, 'name')}
                  </Typography>
                </div>
              </Toolbar>
            </AppBar>

            <div className={classes.profileContainer}>
              <Profile user={activeUser} isModal={true} onClose={() => setActiveUser()} />
            </div>
          </Dialog>

          <Modal {...(modal || {})} fullHeight={get(modal,'title') === 'SET ACCESS'} />

          <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
        </div>
      );
    })
  )
);

const ListWithPages = withInfiniteScroll(UserRow, List);
